import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    getReleaseBannerSelector,
    clearReleaseBanner,
    showLaterWarningAction,
    hideLaterWarningAction,
} from '../../../redux/reducers/reducerSlices/ReleaseBannerReducer';
import { sagaActions } from '../../../sagas/sagaActions';
import { removeInterceptor } from '../../../redux/api/Interceptor';
import './ReleaseBanner.css';

/**
 * ReleaseBanner — Global authenticated-application-level component.
 *
 * Rendered once in App.jsx, gated by `user?.sessionToken`.
 * Reads from `releaseBannerReducer` slice; never fetches data itself.
 *
 * Supports two notificationTypes driven by `bannerData.notificationType`:
 *
 *  ── "Release" (default / legacy) ──────────────────────────────────────────
 *  Two sub-modes driven by `forceUpdate`:
 *  • forceUpdate = true  → non-dismissible banner, single "Update Now" CTA
 *  • forceUpdate = false → banner with "Update", "Later", and "✕" (close) CTAs
 *                          → "Later" reveals a dismissible warning message below
 *                          → "✕"  dismisses the banner entirely
 *
 *  Layout:
 *   [⚠ icon/logo col — left, vertically centered]
 *   [content col — flex column]
 *     Top row: heading + version badge | Update / Later / ✕ buttons
 *     bannerText row (if present)
 *     bannerSubtext row (if present, else version-based default)
 *   [Disclaimer — full-width strip at bottom]
 *   [✕ — absolutely positioned top-right for non-forceUpdate mode]
 *
 *  ── "Notification" ────────────────────────────────────────────────────────
 *  Informational / time-gated banner. Visible only while the current UTC time
 *  falls within [startDateTime, endDateTime]. No update flow.
 *  • severity = 0 → low    — light orange theme
 *  • severity = 1 → medium — orange theme
 *  • severity = 2 → high   — red/urgent theme
 *
 *  Layout:
 *   [⚠ icon/logo col — left, vertically centered]
 *   [content col — flex column]
 *     Top row: heading | ✕ button
 *     bannerText row (if present)
 *     bannerSubtext row (if present)
 *   [Disclaimer — full-width strip at bottom]
 *   [✕ — absolutely positioned top-right]
 *
 *  Real-time appear/disappear:
 *   A 60-second polling interval re-checks whether the current time is still
 *   within [startDateTime, endDateTime] and auto-dispatches clearReleaseBanner()
 *   when the window closes — no page reload needed.
 *
 * @param {boolean} [isPublic=false]
 *   When true the component is rendered on the public login page (no session).
 *   For Release banners clicking "Update" / "Update Now" skips the save-work
 *   confirmation and reloads directly. For Notification banners this prop has
 *   no effect — behaviour is identical on public and authenticated pages.
 */

/**
 * Parses a datetime string as UTC.
 * Handles both "YYYY-MM-DD HH:MM:SS" (space-separated, UTC — as stored in the DB)
 * and ISO "YYYY-MM-DDTHH:MM:SSZ" formats. Appending 'Z' ensures JS Date treats
 * the space-separated format as UTC rather than local time.
 */
const toUtcDate = (dtStr) => {
    if (!dtStr) return null;
    if (/[Zz]$/.test(dtStr) || /[+-]\d{2}:\d{2}$/.test(dtStr)) return new Date(dtStr);
    return new Date(dtStr.replace(' ', 'T') + 'Z');
};

const ReleaseBanner = ({ isPublic = false }) => {
    const dispatch = useDispatch();
    const {
        isVisible,
        forceUpdate,
        bannerData,
        showLaterWarning,
    } = useSelector(getReleaseBannerSelector);

    // Local state: show the "save your work" confirmation before updating.
    // Never shown when isPublic=true (login page — no work to save).
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

    /**
     * Real-time appear/disappear for Notification banners.
     *
     * Notification banners are time-gated: they should appear when the current
     * time enters [startDateTime, endDateTime] and disappear when it exits.
     * Without polling the banner would stay visible/invisible until the next
     * page reload. We run a 60-second interval that re-checks the window and
     * dispatches clearReleaseBanner() when the window has closed.
     *
     * The interval only runs when bannerData.notificationType === 'Notification'.
     * A ref is used to always read the latest bannerData inside the interval
     * without causing the effect to re-subscribe on every render.
     */
    const bannerDataRef = useRef(bannerData);
    bannerDataRef.current = bannerData;

    useEffect(() => {
        if (!bannerData || bannerData.notificationType !== 'Notification') return;

        const checkWindow = () => {
            const data = bannerDataRef.current;
            if (!data || data.notificationType !== 'Notification') return;
            const now = new Date();
            const start = toUtcDate(data.startDateTime);
            const end = toUtcDate(data.endDateTime);
            const afterStart = start ? now >= start : true;
            const beforeEnd = end ? now <= end : true;
            if (!afterStart || !beforeEnd) {
                dispatch(clearReleaseBanner());
            }
        };

        const intervalId = setInterval(checkWindow, 60 * 1000);
        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bannerData?.id, dispatch]);

    /**
     * Public (login) page real-time banner polling.
     * On the login page (isPublic=true) there is no AppSync subscription,
     * so Notification banners that start/end while the page is open would
     * never appear/disappear without a full page reload.
     * Poll the public REST endpoint every 60 seconds.
     */
    useEffect(() => {
        if (!isPublic) return;

        const pollPublicBanner = () => {
            dispatch({ type: sagaActions.FETCH_RELEASE_BANNER_PUBLIC });
        };

        const intervalId = setInterval(pollPublicBanner, 60 * 1000);
        return () => clearInterval(intervalId);
    }, [isPublic, dispatch]);

    // ----- Handlers --------------------------------------------------------

    /**
     * Step 1 — User clicks "Update" / "Update Now".
     * • isPublic=true  → skip confirmation, reload immediately (no work to save).
     * • isPublic=false → show the save-work confirmation panel.
     */
    const handleUpdateClick = () => {
        if (isPublic) {
            window.location.href = '/';
        } else {
            setShowUpdateConfirm(true);
        }
    };

    /**
     * Step 2 — User confirms ("Yes, Update"): full logout + hard reload.
     * Only reachable when isPublic=false (authenticated app).
     */
    const handleConfirmUpdate = () => {
        setShowUpdateConfirm(false);
        removeInterceptor();
        dispatch({ type: sagaActions.CLEAR_SIGNEDIN_USER });
        dispatch({ type: sagaActions.RESET_USER });
        dispatch({ type: sagaActions.CLEAR_SMART_SCHEDULE_REDUCER_DATA });
        dispatch({ type: sagaActions.UNSUBSCRIBE_RELEASE_BANNER });
        window.localStorage.clear();
        window.location.href = '/';
    };

    /** Step 2 (cancel) — dismiss confirmation, keep banner open. */
    const handleCancelUpdate = () => {
        setShowUpdateConfirm(false);
    };

    /** For normal Release update: show the "you chose Later" warning. */
    const handleLater = () => {
        dispatch(showLaterWarningAction());
    };

    /** Close the entire banner. */
    const handleCloseBanner = () => {
        dispatch(clearReleaseBanner());
    };

    /** Dismiss the "Later" warning. */
    const handleCloseLaterWarning = () => {
        dispatch(hideLaterWarningAction());
    };

    // ----- Guard -----------------------------------------------------------
    if (!isVisible || !bannerData) {
        return null;
    }

    // ----- Destructure banner fields ---------------------------------------
    const {
        notificationType,
        bannerHeading,
        bannerText,
        bannerSubtext,
        bannerLogo,
        bannerDisclaimer,
        currentVersion,
        prevVersion,
        severity,
        startDateTime,
        endDateTime,
    } = bannerData;

    // =========================================================================
    // NOTIFICATION banner — time-gated informational banner
    // =========================================================================
    if (notificationType === 'Notification') {
        /**
         * Visibility window check.
         * startDateTime / endDateTime stored as "YYYY-MM-DD HH:MM:SS" (UTC).
         */
        const now = new Date();
        const start = toUtcDate(startDateTime);
        const end = toUtcDate(endDateTime);

        const afterStart = start ? now >= start : true;
        const beforeEnd = end ? now <= end : true;

        if (!afterStart || !beforeEnd) {
            return null;
        }

        // severity: 0 = Low (light orange), 1 = Medium (orange), 2 = High (red)
        const notificationThemeClass =
            severity === 2
                ? 'release-banner--notification-high'
                : severity === 1
                    ? 'release-banner--notification-medium'
                    : 'release-banner--notification-low';

        /**
         * Notification banner layout (matches wireframe):
         *
         *  ┌─────────────────────────────────────────────────┐
         *  │ [⚠] │ Heading                            │ [✕] │  ← top row
         *  │     │ Text (if present)                        │
         *  │     │ Subtext (if present)                     │
         *  ├─────────────────────────────────────────────────┤
         *  │ Disclaimer (full-width strip)                   │
         *  └─────────────────────────────────────────────────┘
         *
         *  ✕ is absolutely positioned at top-right of the banner.
         *  Icon column is left-aligned and vertically centred.
         */
        return (
            <div className="release-banner-wrapper">
                <div
                    className={`release-banner release-banner--notification-wrap ${notificationThemeClass}`}
                    role="banner"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {/* ✕ close button — absolutely positioned at top-right corner */}
                    <button
                        className="release-banner__btn-close release-banner__btn-close--abs"
                        onClick={handleCloseBanner}
                        type="button"
                        aria-label="Dismiss notification banner"
                        title="Dismiss"
                    >
                        ✕
                    </button>

                    {/* Main body: icon col + content col */}
                    <div className="release-banner__body-row">
                        {/* Icon column — left, vertically centered */}
                        <div className="release-banner__icon-col">
                            {bannerLogo
                                ? <img src={bannerLogo} alt="Notification icon" className="release-banner__icon-img" />
                                : <span className="release-banner__icon-fallback" aria-hidden="true">⚠️</span>
                            }
                        </div>

                        {/* Content column */}
                        <div className="release-banner__content-col">
                            {bannerHeading && (
                                <div className="release-banner__heading">
                                    {bannerHeading}
                                </div>
                            )}
                            {bannerText && (
                                <div className="release-banner__text">
                                    {bannerText}
                                </div>
                            )}
                            {bannerSubtext && (
                                <div className="release-banner__subtext">
                                    {bannerSubtext}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Disclaimer — full-width strip at bottom */}
                    {bannerDisclaimer && (
                        <div className="release-banner__disclaimer-strip">
                            {bannerDisclaimer}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // =========================================================================
    // RELEASE banner — version-gated, with Update/Later/✕ actions
    // =========================================================================

    // Version badge: "v0.2.14 → v1.2.14"
    const versionLabel = prevVersion && currentVersion
        ? `v${prevVersion} → v${currentVersion}`
        : currentVersion
            ? `v${currentVersion}`
            : null;

    /**
     * Release banner layout (matches wireframe):
     *
     *  ┌─────────────────────────────────────────────────────────┐
     *  │ [⚠] │ Heading [v badge] │ [Update] [Later] [✕]        │  ← top row
     *  │     │ bannerText (if present)                           │
     *  │     │ bannerSubtext (if present, else default text)     │
     *  ├─────────────────────────────────────────────────────────┤
     *  │ Disclaimer (full-width strip)                           │
     *  └─────────────────────────────────────────────────────────┘
     *
     *  forceUpdate = true  → only "Update Now" button; no ✕, no Later.
     *  forceUpdate = false → Update + Later + ✕ (absolutely positioned top-right).
     */
    return (
        <div className="release-banner-wrapper">
            {/* ── Main Banner Strip ── */}
            <div
                className={`release-banner release-banner--release-wrap ${forceUpdate ? 'release-banner--force' : 'release-banner--normal'}`}
                role="banner"
                aria-live="assertive"
                aria-atomic="true"
            >
                {/* ✕ close button — absolutely positioned top-right (non-forceUpdate only) */}
                {!forceUpdate && (
                    <button
                        className="release-banner__btn-close release-banner__btn-close--abs"
                        onClick={handleCloseBanner}
                        type="button"
                        aria-label="Dismiss release banner"
                        title="Dismiss"
                    >
                        ✕
                    </button>
                )}

                {/* Main body: icon col + content col */}
                <div className="release-banner__body-row">
                    {/* Icon column — left, vertically centered */}
                    <div className="release-banner__icon-col">
                        {bannerLogo
                            ? <img src={bannerLogo} alt="Release icon" className="release-banner__icon-img" />
                            : <span className="release-banner__icon-fallback" aria-hidden="true">⚠️</span>
                        }
                    </div>

                    {/* Content column */}
                    <div className="release-banner__content-col">
                        {/* Top row: heading + version badge + action buttons */}
                        <div className="release-banner__top-row">
                            <div className="release-banner__heading-group">
                                {bannerHeading && (
                                    <span className="release-banner__heading">
                                        {bannerHeading}
                                    </span>
                                )}
                                {/* Version badge inline next to heading */}
                                {versionLabel && (
                                    <span className="release-banner__version-badge">
                                        {versionLabel}
                                    </span>
                                )}
                            </div>

                            {/* Action buttons — right-aligned in top row */}
                            <div className="release-banner__actions">
                                {forceUpdate ? (
                                    <button
                                        className="release-banner__btn release-banner__btn--update"
                                        onClick={handleUpdateClick}
                                        type="button"
                                    >
                                        Update Now
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="release-banner__btn release-banner__btn--update"
                                            onClick={handleUpdateClick}
                                            type="button"
                                        >
                                            Update
                                        </button>
                                        <button
                                            className="release-banner__btn release-banner__btn--later"
                                            onClick={handleLater}
                                            type="button"
                                        >
                                            Later
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* bannerText — show if present */}
                        {bannerText && (
                            <div className="release-banner__text">
                                {bannerText}
                            </div>
                        )}

                        {/* bannerSubtext — show if present, else version-based fallback */}
                        {bannerSubtext ? (
                            <div className="release-banner__subtext">
                                {bannerSubtext}
                            </div>
                        ) : currentVersion ? (
                            <div className="release-banner__subtext">
                                Please update to version {currentVersion} to enjoy the latest improvements.
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* Disclaimer — full-width strip at bottom */}
                {bannerDisclaimer && (
                    <div className="release-banner__disclaimer-strip">
                        {bannerDisclaimer}
                    </div>
                )}
            </div>

            {/* ── Save-work Confirmation Panel ── */}
            {showUpdateConfirm && (
                <div
                    className="release-banner__confirm"
                    role="alertdialog"
                    aria-modal="false"
                    aria-label="Save work before updating"
                >
                    <div className="release-banner__confirm-inner">
                        <div className="release-banner__confirm-icon" aria-hidden="true">
                            💾
                        </div>
                        <div className="release-banner__confirm-text">
                            <strong>Please save your work before updating</strong>
                            <p>
                                Clicking <em>Yes, Update</em> will log you out and reload the
                                application to apply the latest version. Any unsaved changes will
                                be lost.
                            </p>
                        </div>
                        <div className="release-banner__confirm-actions">
                            <button
                                className="release-banner__btn release-banner__btn--confirm-yes"
                                onClick={handleConfirmUpdate}
                                type="button"
                            >
                                Yes, Update
                            </button>
                            <button
                                className="release-banner__btn release-banner__btn--confirm-cancel"
                                onClick={handleCancelUpdate}
                                type="button"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── "Later" Warning — rendered below the banner, inside the wrapper ── */}
            {!forceUpdate && showLaterWarning && (
                <div
                    className="release-banner__later-warning"
                    role="alertdialog"
                    aria-modal="false"
                    aria-label="Update deferred warning"
                >
                    <div className="release-banner__later-warning-inner">
                        <div className="release-banner__later-warning-icon" aria-hidden="true">
                            ⚠️
                        </div>
                        <div className="release-banner__later-warning-text">
                            <strong>Update deferred</strong>
                            <p>
                                You have chosen to update later. Some features may not work
                                correctly until you reload the application to apply the latest
                                changes.
                            </p>
                        </div>
                        <button
                            className="release-banner__later-warning-close"
                            onClick={handleCloseLaterWarning}
                            type="button"
                            aria-label="Close warning"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReleaseBanner;
